import {cva, type VariantProps} from 'class-variance-authority'

export const buttonVariants = cva(
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center u-body-sm font-bold whitespace-nowrap outline-none select-none focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // The dark stop is --brand-gradient-end, derived from --primary-light
        // in globals.css, NOT --primary. See the comment on the derivation for
        // why those must stay separate.
        //
        // Read the RAW token here, never the `--color-brand-gradient-end`
        // alias. The alias is declared once, in `@theme inline` on `:root`, and
        // a custom property holding `var()` is substituted on the element that
        // declares it — so the alias freezes to Jøtul's orange and every
        // descendant `.brand-*` scope inherits that frozen value. The
        // equivalent Tailwind utility is safe for the same reason in reverse:
        // `to-brand-gradient-end` compiles to `var(--brand-gradient-end)`,
        // because `@theme inline` inlines the value rather than emitting the
        // alias. Matching that is what keeps this declaration honest.
        default:
          'bg-[linear-gradient(to_right,var(--color-primary-light),var(--brand-gradient-end),var(--brand-gradient-end))] bg-[length:200%_100%] bg-left text-white shadow transition-[background-position] duration-300 hover:bg-right',
        secondary:
          'bg-white text-foreground border border-foreground shadow transition-colors duration-300 hover:bg-muted',
        outline:
          'border border-foreground bg-white text-foreground transition-colors hover:bg-background',
        ghost:
          'hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50',
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40',
        glass:
          'bg-[rgba(255,255,255,0.2)] text-foreground backdrop-blur-sm shadow transition-opacity hover:opacity-90',
        link: '!h-auto !px-0 text-primary underline underline-offset-3 decoration-1 transition-[text-decoration-thickness] duration-200 hover:decoration-2',
      },
      size: {
        default:
          'h-[45px] gap-2 rounded-full px-[30px] has-data-[icon=inline-end]:pr-5 has-data-[icon=inline-start]:pl-5',
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
        icon: 'size-8',
        'icon-xs':
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        'icon-sm':
          'size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg',
        'icon-lg': 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export type ButtonVariantProps = VariantProps<typeof buttonVariants>
